'use client';

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { Icon } from '../Icon';
import { cn } from '@/app/lib';
import { IconMap } from '../IconMap';
import { ShareIcon } from '@workspace/interfaces';
import { usePathname, useSearchParams } from 'next/navigation';
import { toast } from 'sonner';
import { Typography } from '..';

export interface ShareIconsProps {
  icons: ShareIcon[];
  className?: string;
  socialLinksData?: ShareIcon[]; // API data for social links
}

function ShareIconComponent({
  shareType,
  iconUrl,
  size = 24,
}: {
  shareType: string;
  iconUrl?: string;
  size?: number;
  sharePath?: string;
}) {
  // Map shareType to IconMap keys (same as SocialLinks)
  const getIconName = (shareType: string): keyof typeof IconMap => {
    const type = shareType.toLowerCase();
    switch (type) {
      case 'facebook':
        return 'facebook';
      case 'linkedin':
        return 'linkedin';
      case 'linked':
        return 'linked';
      case 'pinterest':
        return 'pinterest';
      case 'share':
        return 'share';
      default:
        return shareType as keyof typeof IconMap;
    }
  };

  const iconName = getIconName(shareType);

  if (iconUrl) {
    return (
      <Image src={iconUrl} alt={`${shareType} share icon`} width={size} height={size} className="object-contain" />
    );
  }

  return <Icon name={iconName} size={size} />;
}

export function ShareIcons({ icons, className = '' }: ShareIconsProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isMobile, setIsMobile] = useState(false);
  const searchParamsString = searchParams?.toString() ?? '';
  const pathWithSearch = `${pathname || ''}${searchParamsString ? `?${searchParamsString}` : ''}`;
  const [currentUrl, setCurrentUrl] = useState<string>(pathWithSearch);
  const [pageTitle, setPageTitle] = useState<string>('');

  useEffect(() => {
    setIsMobile(/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent));
  }, []);
  // const safeSocialLinksData = Array.isArray(socialLinksData) ? socialLinksData : [];

  useEffect(() => {
    const updateShareContext = () => {
      if (typeof window === 'undefined') {
        return;
      }

      const nextUrl = `${window.location.origin}${pathWithSearch}`;
      setCurrentUrl(nextUrl);
      setPageTitle(document.title || '');
    };

    updateShareContext();
  }, [pathWithSearch]);

  const buildShareHref = (shareIcon: ShareIcon): string | undefined => {
    const type = (shareIcon.shareType || '').toLowerCase();
    const shareUrl = currentUrl;
    // Handle copy/link first: these should NOT navigate
    if (type === 'linked' || type === 'link' || type === 'copy') {
      return undefined;
    }

    // Handle email/share next regardless of CMS config
    if (type === 'share' || type === 'gmail' || type === 'email' || type === 'mail') {
      const subject = pageTitle ? `${pageTitle}` : '';
      const body = `Check out:\n${shareUrl}`;
      return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    }

    // Priority 3: Auto-generate share URLs for common platforms
    if (type === 'linkedin') {
      // Use LinkedIn shareArticle endpoint for broader mobile compatibility
      const linkedinUrl = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}`;
      return linkedinUrl;
    }

    if (type === 'facebook') {
      const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
      return facebookUrl;
    }

    if (type === 'pinterest') {
      const pinterestUrl = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(shareUrl)}`;
      return pinterestUrl;
    }

    // Fallback to provided path
    return shareIcon.sharePath;
  };

  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const copyToastTimerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (copyToastTimerRef.current !== null) {
        window.clearTimeout(copyToastTimerRef.current);
      }
    };
  }, []);

  const showCopyToast = (index: number) => {
    if (copyToastTimerRef.current !== null) {
      window.clearTimeout(copyToastTimerRef.current);
    }
    setCopiedIndex(index);
    copyToastTimerRef.current = window.setTimeout(() => {
      setCopiedIndex(null);
    }, 2000);
  };

  const handleCopyUrl = async (event: React.MouseEvent<HTMLButtonElement>, index: number) => {
    const shareUrl = currentUrl;
    try {
      if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // Fallback for non-secure contexts/older browsers
        const textarea = document.createElement('textarea');
        textarea.value = shareUrl;
        textarea.setAttribute('readonly', '');
        textarea.style.position = 'absolute';
        textarea.style.left = '-9999px';
        document.body.appendChild(textarea);
        textarea.select();
        document.body.removeChild(textarea);
      }
      showCopyToast(index);
    } catch (err) {
      console.error(err);
      toast.error('Unable to copy link, please try again.');
    }
  };

  return (
    <div className={cn('flex', className)}>
      {icons?.map((shareIcon, index) => {
        const href = buildShareHref(shareIcon);
        const type = (shareIcon.shareType || '').toLowerCase();
        const icon = (
          <ShareIconComponent shareType={shareIcon.shareType} iconUrl={shareIcon.iconUrl} sharePath={href} size={24} />
        );

        // Special case for copy/link functionality
        if (type === 'linked' || type === 'link' || type === 'copy') {
          return (
            <div key={index} className="relative mr-4 last:mr-0 flex flex-col items-center">
              <button
                type="button"
                onClick={(event) => handleCopyUrl(event, index)}
                className="hover:opacity-80 transition-opacity cursor-pointer bg-transparent border-none p-0"
                aria-label={`Copy URL`}
              >
                {icon}
              </button>
              {copiedIndex === index && (
                <div className="absolute top-12 mt-sm flex -translate-y-1/2 items-center gap-1 rounded-sm bg-secondary-50 px-3 py-sm w-max!">
                  <span className="flex h-6 w-6 items-center justify-center">
                    <Icon name="Linked" size={24} className="text-primary" />
                  </span>
                  <Typography variant="bodySm" className="text-black font-body! text-base!">
                    Link copied
                  </Typography>
                </div>
              )}
            </div>
          );
        }

        if (href?.trim()) {
          const isExternal = href.startsWith('http') || href.startsWith('mailto:');
          const isAnchor = href.startsWith('#');

          if (isAnchor) {
            const anchorHref = href.startsWith('#') ? href : `#${href}`;
            return (
              <a
                key={index}
                href={anchorHref}
                className="hover:opacity-80 transition-opacity mr-4 last:mr-0"
                aria-label={`Share via ${shareIcon.shareType}`}
              >
                {icon}
              </a>
            );
          }

          const isLinkedIn = type === 'linkedin';
          // Only use Web Share API for a generic "share" icon, not for LinkedIn
          const handleMobileShare = async (e: React.MouseEvent) => {
            if (type !== 'share') return;
            if (isMobile && navigator.share) {
              e.preventDefault();
              try {
                await navigator.share({
                  title: pageTitle,
                  url: currentUrl,
                });
              } catch (err) {
                // Fallback to normal link if sharing fails
                window.open(href, '_blank');
                console.error(err);
              }
            }
          };

          return (
            // eslint-disable-next-line react/jsx-no-target-blank
            <a
              key={index}
              href={href}
              target={isExternal ? '_blank' : '_self'}
              rel={isExternal ? (isLinkedIn ? 'noopener' : 'noopener noreferrer') : undefined}
              className="hover:opacity-80 transition-opacity mr-4 last:mr-0"
              aria-label={`Share via ${shareIcon.shareType}`}
              onClick={isLinkedIn ? undefined : handleMobileShare}
            >
              {icon}
            </a>
          );
        }

        // No share path, just icon
        return (
          <div key={index} className="mr-4 last:mr-0">
            {icon}
          </div>
        );
      })}
    </div>
  );
}
